import {useState} from 'react'
import {
  updateNationalSponsorAssignments,
  updateSponsorAssignments
} from '../api/sponsorAssignments'
import {AppState} from '../store/store'
import {
  LessonSponsorAssignment,
  ModuleSponsorAssignment
} from '../types/sponsorAssignments'

type AssignSponsorParams =
  | {type: 'national'; assignments: ModuleSponsorAssignment[]; state: AppState}
  | {
      type: 'community'
      sponsorId: string
      communityId: string
      assignments: LessonSponsorAssignment[]
      state: AppState
    }

type Options = Readonly<{
  onError?: (error: Error) => void
  onSuccess?: () => void
}>

type UseAssignSponsorReturn = {
  assignSponsor: (params: AssignSponsorParams) => void
  isAssigning: boolean
  error: Error | null
}

export function useAssignSponsor({
  onError,
  onSuccess
}: Options = {}): UseAssignSponsorReturn {
  const [isAssigning, setIsAssigning] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const assignSponsor = (params: AssignSponsorParams): void => {
    setIsAssigning(true)
    setError(null)

    const promise =
      params.type === 'national'
        ? updateNationalSponsorAssignments(params.assignments, params.state)
        : updateSponsorAssignments(
            params.communityId,
            params.assignments,
            params.state
          )

    promise
      .then(() => onSuccess?.())
      .catch(err => {
        const error =
          err instanceof Error ? err : new Error('Failed to assign sponsor')
        setError(error)
        onError?.(error)
      })
      .finally(() => setIsAssigning(false))
  }

  return {
    assignSponsor,
    isAssigning,
    error
  }
}
